import type { Settings } from './settings';

/*
 * Strukturierte Daten (schema.org) aus den Firmendaten.
 * Änderungen unter „Firmendaten“ im Admin wirken automatisch auch hier.
 */

export const SITE_URL = 'https://top-bhs.de';
const LOGO = `${SITE_URL}/favicon/favicon-256.png`;
const LEGAL_NAME = 'TOP Buchhaltungsservice UG (haftungsbeschränkt)';

const OPENING_HOURS = [
  { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'], opens: '09:00', closes: '18:00' },
];

/** "+49 231 53479212" → "+4923153479212" */
export const e164 = (phone: string): string => phone.replace(/[^+\d]/g, '');

/** "Stuttgartstraße 15-17, 44143 Dortmund" → Straße, PLZ, Ort */
export function parseAddress(address: string): { street: string; zip: string; city: string } {
  const m = address.match(/^(.*?),\s*(\d{5})\s+(.+)$/);
  return m ? { street: m[1].trim(), zip: m[2], city: m[3].trim() } : { street: address, zip: '', city: 'Dortmund' };
}

const phones = (s: Settings): string[] => [s.phone_landline, s.phone].filter(Boolean).map(e164);
export const socialUrls = (s: Settings): string[] => s.social_links.map((l) => l.url).filter((u) => /^https?:\/\//.test(u));

function postalAddress(s: Settings) {
  const a = parseAddress(s.address);
  return { '@type': 'PostalAddress', streetAddress: a.street, postalCode: a.zip, addressLocality: a.city, addressCountry: 'DE' };
}

function contactPoints(s: Settings) {
  return phones(s).map((telephone) => ({
    '@type': 'ContactPoint',
    telephone,
    contactType: 'customer service',
    areaServed: 'DE',
    availableLanguage: ['German', 'Persian', 'English', 'Arabic'],
  }));
}

/** LocalBusiness (AccountingService) – auf allen Seiten */
export function localBusinessSchema(s: Settings) {
  const sameAs = socialUrls(s);
  return {
    '@context': 'https://schema.org',
    '@type': 'AccountingService',
    '@id': `${SITE_URL}/#business`,
    name: s.company_name,
    legalName: LEGAL_NAME,
    alternateName: 'TOP BHS',
    url: `${SITE_URL}/`,
    logo: LOGO,
    image: LOGO,
    description:
      'Buchhaltungsservice in Dortmund für laufende Buchhaltung, Lohnabrechnung und betriebswirtschaftliche Auswertungen – persönlich, digital und mehrsprachig.',
    telephone: phones(s)[0],
    email: s.email,
    address: postalAddress(s),
    geo: { '@type': 'GeoCoordinates', latitude: 51.5136, longitude: 7.4653 },
    hasMap: s.maps_url,
    openingHoursSpecification: OPENING_HOURS,
    areaServed: { '@type': 'City', name: 'Dortmund' },
    knowsLanguage: ['de', 'fa', 'en', 'ar'],
    contactPoint: contactPoints(s),
    priceRange: '€€',
    currenciesAccepted: 'EUR',
    paymentAccepted: 'Überweisung, Lastschrift',
    memberOf: { '@type': 'Organization', name: 'Bundesverband Selbständige Buchhalter und Bilanzbuchhalter (BBH)' },
    ...(sameAs.length ? { sameAs } : {}),
  };
}

/** Organization – zusätzlich auf der Startseite */
export function organizationSchema(s: Settings) {
  const sameAs = socialUrls(s);
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': `${SITE_URL}/#organization`,
    name: s.company_name,
    legalName: LEGAL_NAME,
    url: `${SITE_URL}/`,
    logo: LOGO,
    email: s.email,
    address: postalAddress(s),
    contactPoint: contactPoints(s),
    ...(sameAs.length ? { sameAs } : {}),
  };
}
